import { Field } from './Field';
import { TimeSelect } from './TimeSelect';
import { PS, T } from '../lib/theme';
import { POLICY, POLICY_MIN_TIME, POLICY_MAX_START_TIME } from '../lib/policy';
import { slotWindowError } from '../lib/coach-session-window';

interface Props {
  startTime: string;
  endTime: string;
  onStartTimeChange: (next: string) => void;
  onEndTimeChange: (next: string) => void;
}

/**
 * The Start/End time pair every coach create form uses, with the policy rules
 * attached in one place: `POLICY.SLOT_STEP_MINUTES` alignment, the legal start
 * range, and `includeMax` so the end dropdown offers the club's `24:00`
 * end-of-day boundary (which `<input type="time">` cannot represent).
 *
 * Both `NewCoachSeriesPage` and `NewOneTimeCoachSessionPage` render this, so a
 * change to the club's slot policy lands in both without either restating it.
 * The shape rule itself is `slotWindowError` — shown here, and re-checked by the
 * caller before submit (a form must not rely on its child for a gate).
 */
export function SlotWindowFields({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
}: Props) {
  const error = slotWindowError(startTime, endTime);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Field label="Start time">
          <TimeSelect
            value={startTime}
            onChange={onStartTimeChange}
            min={POLICY_MIN_TIME}
            max={POLICY_MAX_START_TIME}
            stepMinutes={POLICY.SLOT_STEP_MINUTES}
            ariaLabel="Start time"
          />
        </Field>
        <Field label="End time">
          <TimeSelect
            value={endTime}
            onChange={onEndTimeChange}
            min={POLICY_MIN_TIME}
            max="24:00"
            includeMax={true}
            stepMinutes={POLICY.SLOT_STEP_MINUTES}
            ariaLabel="End time"
          />
        </Field>
      </div>
      {error && (
        <div role="alert" style={{ ...T.caption, color: PS.errorText }}>
          {error}
        </div>
      )}
    </div>
  );
}
